import type { Web3TxMultiCalcSWR, Web3TxMultiCalculateRoutes } from "@/hooks/tx-flow/useDepositCalculate";
import type { Networkish, SupWeb3WithdrawSingleVault } from "@/types";
import { useMemo } from "react";

import { useWeb3DebugContext } from "@/components/Web3/Debug/State";
import useValidateTxSettings from "@/hooks/tx-flow/settings/useValidateTxSettings";
import useIsErc20Withdrawal from "@/hooks/tx-flow/useIsErc20Withdrawal";

import useGetRefundAddress from "@/hooks/web3/useGetRefundAddress";
import useApi from "@/hooks/api/useApi";
import useWeb3UserAddress from "@/hooks/web3/useWeb3UserAddress";
import { useStoredData } from "@/store/Context";
import { cutFractionalPartFromAmount, getVaultDecimals } from "@/utils";
import { getAddress, parseUnits } from "viem";

/**
 * @package api:go
 * @link https://superform-xyz.postman.co/workspace/Superform~fe57d318-f198-4f97-9c50-0b2a5a78dbac/request/3472327-0a1caf47-1620-4c81-9686-8e302902f47e?ctx=documentation
 */
export default function useWithdrawCalculate(
  targetToken:
    | {
        address: string;
        chainId: Networkish;
      }
    | undefined,
  vaultList: SupWeb3WithdrawSingleVault[],
  pauseRequests: boolean = false,
  enabled: boolean = true
): Web3TxMultiCalcSWR {
  const storedData = useStoredData();
  const userAddress = useWeb3UserAddress();

  const isERC20 = useIsErc20Withdrawal();

  const { addCalculate } = useWeb3DebugContext();

  const { address: refundAddress } = useGetRefundAddress(storedData.withdrawalChainId);
  const isAmountsReady = useMemo(() => {
    const filteredAmountVaults = vaultList.filter(singleVault => !!singleVault.amount);
    return filteredAmountVaults.length === vaultList.length;
  }, [vaultList]);

  const { validatedSettings } = useValidateTxSettings(storedData.txSettings);

  const queryParams = useMemo(() => {
    return vaultList.map(singleVault => {
      const decimals = getVaultDecimals(singleVault);
      const amountIn = decimals > 18 ? cutFractionalPartFromAmount(`${singleVault.amount}`) : parseUnits(`${singleVault.amount}`, decimals);
      const isRetain4626 = singleVault.recommend_vault_share_withdrawal || validatedSettings?.retain4626;
      const toTokenAddress = isRetain4626 ? singleVault.assets[0].contract_address : targetToken ? getAddress(targetToken.address) : undefined;

      return {
        bridge_slippage: validatedSettings?.bridgeSlippage,
        exclude_ambs: validatedSettings?.services.ambs,
        exclude_bridges: validatedSettings?.services.bridges,
        exclude_dexes: validatedSettings?.services.dexes,
        exclude_liquidity_providers: validatedSettings?.services.liquidity_providers,
        is_erc20: isERC20,
        refund_address: userAddress,
        retain_4626: isRetain4626,
        route_type: validatedSettings?.routePriority,
        superform_id: singleVault.superposition_id,
        superpositions_amount_in: amountIn,
        superpositions_chain_id: storedData.withdrawalChainId,
        swap_slippage: validatedSettings?.dexSlippage,
        to_chain_id: targetToken?.chainId,
        to_token_address: toTokenAddress,
        user_address: userAddress,
        vault_id: singleVault.id,
      };
    });
  }, [
    vaultList,
    userAddress,
    validatedSettings?.bridgeSlippage,
    validatedSettings?.dexSlippage,
    validatedSettings?.routePriority,
    validatedSettings?.services.ambs,
    validatedSettings?.services.dexes,
    validatedSettings?.services.bridges,
    validatedSettings?.services.liquidity_providers,
    validatedSettings?.retain4626,
    targetToken,
    storedData.withdrawalChainId,
    isERC20,
  ]);

  const isEnabled = useMemo(() => {
    const rawEnabled = !!userAddress && isAmountsReady && !pauseRequests && enabled && targetToken !== undefined && !!refundAddress && !!validatedSettings;
    if (rawEnabled) {
      addCalculate?.(queryParams);
    }
    return rawEnabled;
  }, [userAddress, isAmountsReady, pauseRequests, enabled, targetToken, refundAddress, validatedSettings, addCalculate, queryParams]);

  const { data, isLoading, isError, error, mutate } = useApi<Web3TxMultiCalculateRoutes[], Web3TxMultiCalculateRoutes[]>(
    {
      path: "/withdraw/calculate/",
      api: "v2",
      data: queryParams,
      method: "POST",
      useJwt: true,
      forceJwt: false,
    },
    {
      enabled: isEnabled,
      strategy: "cached",
    }
  );

  const timestamp = useMemo(() => (data !== undefined ? new Date().getTime() : undefined), [data]);

  return {
    data,
    timestamp,
    mutate,
    error,
    isLoading,
    isError,
  };
}
